// SEM-7205 (P4) — /settings/org-dimensions API client.
//
// Tenant org dimension catalogue (spec 68 / ADR-012): orthogonal chart tags,
// NOT a second department tree. Yaratma ucu `idempotency: required` ilan
// ediyor ve anahtarı GERÇEKTEN arıyor; yama ve arşivleme başlığı okumuyor, o
// yüzden onların tekrarı bir cevabı yeniden oynatmaz.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";

export type OrgDimensionKind = "string" | "enum" | "hierarchy_small";

export interface OrgDimensionRow {
  readonly dimension_id: string;
  readonly tenant_id: string;
  readonly name: string;
  readonly slug: string;
  readonly description: string | null;
  readonly kind: OrgDimensionKind | string;
  readonly allowed_values: unknown;
  readonly display_in_chart: boolean;
  readonly display_order: number;
  readonly state: "active" | "archived" | string;
  readonly created_by: string;
  readonly created_at: string | null;
  readonly archived_at: string | null;
}

/** Liste satırı + kaç çalışanın bugün bu boyutta bir değeri olduğu (spec 42:
 *  kapsam sütunu ve arşiv onayı). */
export interface OrgDimensionListRow extends OrgDimensionRow {
  readonly employee_coverage_count: number;
}

export interface ListOrgDimensionsParams {
  readonly state?: "active" | "archived" | "all";
  readonly display_in_chart?: boolean;
}

export interface CreateOrgDimensionInput {
  readonly name: string;
  readonly slug: string;
  readonly description?: string | null;
  readonly kind: OrgDimensionKind;
  readonly allowed_values?: readonly string[];
  readonly display_in_chart?: boolean;
  readonly display_order?: number;
}

// `slug` yamada YOK: slug, çalışan satırındaki `org_dimension_values`
// haritasının anahtarıdır — yeniden adlandırmak yazılmış her değeri öksüz
// bırakırdı (uç de reddeder).
export interface PatchOrgDimensionInput {
  readonly name?: string;
  readonly description?: string | null;
  readonly allowed_values?: readonly string[];
  readonly display_in_chart?: boolean;
  readonly display_order?: number;
}

type DimensionsQuery = {
  state?: string;
  display_in_chart?: boolean;
  with_employee_coverage?: boolean;
};

type DimensionsGet = (
  path: "/v1/org/dimensions",
  init?: { params?: { query?: DimensionsQuery } },
) => Promise<EnvelopedFetchResult<{ items: OrgDimensionListRow[] }>>;

async function listDimensions(
  params: ListOrgDimensionsParams,
  withCoverage: boolean,
): Promise<OrgDimensionListRow[]> {
  const get = platformGraph.GET as unknown as DimensionsGet;
  const query: DimensionsQuery = {};
  if (params.state !== undefined) query.state = params.state;
  if (params.display_in_chart !== undefined) query.display_in_chart = params.display_in_chart;
  if (withCoverage) query.with_employee_coverage = true;

  const result = await unwrapEnvelope(get("/v1/org/dimensions", { params: { query } }));
  return result.items;
}

export async function fetchOrgDimensions(
  params: ListOrgDimensionsParams = {},
): Promise<OrgDimensionRow[]> {
  return listDimensions(params, false);
}

/** Katalog + kapsama sayısı. Ayrı çağrı, çünkü sayım kiracının çalışan
 *  tablosunu tarıyor: personel formu katalogu her açılışta çekiyor ve o sayıyı
 *  hiç okumuyor — okuyan yalnız yönetici ekranıdır: liste sütunu ve arşiv
 *  onayı. */
export async function fetchOrgDimensionsWithCoverage(
  params: ListOrgDimensionsParams = {},
): Promise<OrgDimensionListRow[]> {
  return listDimensions(params, true);
}

type DimensionPost = (
  path: "/v1/org/dimensions",
  init: { body: CreateOrgDimensionInput; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ dimension: OrgDimensionRow }>>;

export async function createOrgDimension(
  input: CreateOrgDimensionInput,
  idempotencyKey: string,
): Promise<OrgDimensionRow> {
  const post = platformGraph.POST as unknown as DimensionPost;
  const result = await unwrapEnvelope(
    post("/v1/org/dimensions", {
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.dimension;
}

type DimensionPatch = (
  path: "/v1/org/dimensions/{id}",
  init: {
    params: { path: { id: string } };
    body: PatchOrgDimensionInput;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ dimension: OrgDimensionRow }>>;

export async function patchOrgDimension(
  dimensionId: string,
  input: PatchOrgDimensionInput,
  idempotencyKey: string,
): Promise<OrgDimensionRow> {
  const patch = platformGraph.PATCH as unknown as DimensionPatch;
  const result = await unwrapEnvelope(
    patch("/v1/org/dimensions/{id}", {
      params: { path: { id: dimensionId } },
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.dimension;
}

type DimensionArchivePost = (
  path: "/v1/org/dimensions/{id}/archive",
  init: { params: { path: { id: string } }; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ dimension: OrgDimensionRow }>>;

export async function archiveOrgDimension(
  dimensionId: string,
  idempotencyKey: string,
): Promise<OrgDimensionRow> {
  const post = platformGraph.POST as unknown as DimensionArchivePost;
  const result = await unwrapEnvelope(
    post("/v1/org/dimensions/{id}/archive", {
      params: { path: { id: dimensionId } },
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.dimension;
}

/** `allowed_values` yalnız enum boyutunda anlamlı; hierarchy_small ağacı bu
 *  ekranda düzenlenmiyor (ekran spec'i onu departman ağacına yönlendiriyor). */
export function parseAllowedValues(raw: string): readonly string[] {
  return raw
    .split(/[\n,]/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

/**
 * Bir boyutun izin verilen değerlerini seçenek listesine düzler.
 *
 * enum: düz dize dizisi. hierarchy_small: `{ value, children }` düğümleri ve uç
 * değeri HERHANGİ bir düğüme (yaprak ya da dal) karşı ölçüyor — bu yüzden ağaç
 * derinlemesine geziliyor. string: liste yok, `[]`.
 */
export function dimensionValueOptions(value: unknown): readonly string[] {
  const out: string[] = [];
  const walk = (nodes: unknown): void => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (typeof node === "string") {
        out.push(node);
        continue;
      }
      if (node === null || typeof node !== "object") continue;
      const record = node as { value?: unknown; children?: unknown };
      if (typeof record.value === "string") out.push(record.value);
      walk(record.children);
    }
  };
  walk(value);
  return out;
}

export function allowedValuesToText(value: unknown): string {
  if (!Array.isArray(value)) return "";
  return value.filter((v): v is string => typeof v === "string").join(", ");
}
